// M137 (2026-05-10) — Admin Exam Settings Page
// 2 tab:
//   - "Ngưỡng đạt": chỉnh threshold (% câu BINARY đúng) cho 3 bộ thưởng.
//   - "Câu trả lời NV": list attempts paginated + click xem chi tiết per câu.
// NV không thấy admin xem — silent oversight (endpoint không expose lên UI NV).

import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { boLabel } from '../lib/bo.js';

const REWARD_SETS = ['bo_1000k', 'bo_1250k', 'bo_nang_cao'];
const STATUS_LABELS = {
  in_progress: 'Đang làm',
  submitted: 'Đã nộp',
  regraded: 'Đã chấm lại',
  abandoned: 'Đã huỷ',
};

export default function AdminExamSettingsPage() {
  // M142 (2026-05-12): default tab oversight (chủ vào đây chủ yếu để xem câu trả lời NV)
  const [tab, setTab] = useState('oversight');

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <div className="mb-4 flex items-center gap-3">
        <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700">← Trang admin</Link>
        <h1 className="text-2xl font-semibold">Xem nhân viên trả lời kiểm tra</h1>
      </div>

      <div className="border-b mb-4 flex gap-1">
        {/* M142: tab "Câu trả lời NV" nằm trước "Ngưỡng đạt" */}
        <TabButton active={tab === 'oversight'} onClick={() => setTab('oversight')}>
          Câu trả lời NV
        </TabButton>
        <TabButton active={tab === 'thresholds'} onClick={() => setTab('thresholds')}>
          Ngưỡng đạt
        </TabButton>
      </div>

      {tab === 'oversight' && <OversightTab />}
      {tab === 'thresholds' && <ThresholdsTab />}
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={
        'px-4 py-2 text-sm font-medium border-b-2 -mb-px ' +
        (active
          ? 'border-emerald-600 text-emerald-700'
          : 'border-transparent text-slate-500 hover:text-slate-700')
      }
    >
      {children}
    </button>
  );
}

// ============================================================
// Tab 1: Ngưỡng đạt
// ============================================================
function ThresholdsTab() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState({ bo_1000k: 95, bo_1250k: 95, bo_nang_cao: 95 });
  // M141 (2026-05-12): ngưỡng điểm/câu để coi là đúng
  const [perQPass, setPerQPass] = useState(60);
  // M151 (2026-05-13): số ngày deadline làm bài
  const [deadlineDays, setDeadlineDays] = useState(4);
  // M315 (2026-05-27): số lượt thi/tuần
  const [attemptsPerWeek, setAttemptsPerWeek] = useState(2);

  useEffect(() => {
    api.adminExamSettings()
      .then((res) => {
        setValues(res.thresholds || {});
        if (res.per_question_pass != null) setPerQPass(res.per_question_pass);
        if (res.deadline_days != null) setDeadlineDays(res.deadline_days);
        if (res.attempts_per_week != null) setAttemptsPerWeek(res.attempts_per_week);
      })
      .catch((err) => toast.error(err?.data?.error || 'Lỗi tải ngưỡng đạt'))
      .finally(() => setLoading(false));
  }, []);

  async function handleSave() {
    setSaving(true);
    try {
      const res = await api.adminExamSettingsUpdate({
        ...values,
        per_question_pass: perQPass,
        deadline_days: deadlineDays,
        attempts_per_week: attemptsPerWeek,
      });
      setValues(res.thresholds);
      if (res.per_question_pass != null) setPerQPass(res.per_question_pass);
      if (res.deadline_days != null) setDeadlineDays(res.deadline_days);
      if (res.attempts_per_week != null) setAttemptsPerWeek(res.attempts_per_week);
      toast.success('Đã lưu ngưỡng đạt');
    } catch (err) {
      toast.error(err?.data?.error || 'Lỗi lưu');
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div className="text-slate-500">Đang tải...</div>;

  return (
    <div className="bg-white rounded-lg border p-5 max-w-2xl space-y-6">
      {/* Section 1: ngưỡng điểm/câu */}
      <div>
        <h3 className="text-sm font-semibold text-slate-800 mb-2">1. Điểm tối thiểu mỗi câu để coi là đúng</h3>
        <p className="text-sm text-slate-600 mb-3">
          AI chấm mỗi câu 0-100 điểm. Câu được coi là <strong>đúng</strong> khi điểm AI chấm ≥ ngưỡng này.
          Mặc định 60. Nâng lên 70 → chấm chặt hơn, hạ xuống 50 → nới lỏng.
        </p>
        <div className="flex items-center gap-3">
          <label className="w-48 text-sm font-medium text-slate-700">Ngưỡng điểm/câu</label>
          <input
            type="number"
            min={0}
            max={100}
            value={perQPass}
            onChange={(e) => setPerQPass(parseInt(e.target.value, 10) || 0)}
            className="w-24 border rounded px-3 py-1.5 text-right"
          />
          <span className="text-sm text-slate-500">/ 100</span>
        </div>
      </div>

      <div className="border-t"></div>

      {/* Section 2: ngưỡng % câu đúng */}
      <div>
        <h3 className="text-sm font-semibold text-slate-800 mb-2">2. Tỉ lệ câu đúng tối thiểu để Đạt phiên</h3>
        <p className="text-sm text-slate-600 mb-3">
          Ngưỡng % câu đúng (mỗi câu ≥{perQPass} điểm AI chấm) trên tổng câu của phiên.
          Ví dụ phiên 119 câu, ngưỡng 95% → cần ≥114 câu đúng.
        </p>
        <div className="space-y-4">
          {REWARD_SETS.map((bo) => (
            <div key={bo} className="flex items-center gap-3">
              <label className="w-48 text-sm font-medium text-slate-700">{boLabel(bo)}</label>
              <input
                type="number"
                min={0}
                max={100}
                value={values[bo] ?? 95}
                onChange={(e) => setValues({ ...values, [bo]: parseInt(e.target.value, 10) || 0 })}
                className="w-24 border rounded px-3 py-1.5 text-right"
              />
              <span className="text-sm text-slate-500">%</span>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t"></div>

      {/* M151 (2026-05-13): Section 3 — số ngày deadline */}
      <div>
        <h3 className="text-sm font-semibold text-slate-800 mb-2">3. Hạn hoàn thành bài kiểm tra</h3>
        <p className="text-sm text-slate-600 mb-3">
          NV phải hoàn thành bài kiểm tra trong số ngày này kể từ lúc bắt đầu. Quá hạn → tự huỷ → NV phải làm lại từ đầu.
          Mặc định <strong>4 ngày</strong>. Tối thiểu 1, tối đa 30.
        </p>
        <div className="flex items-center gap-3">
          <label className="w-48 text-sm font-medium text-slate-700">Số ngày tối đa</label>
          <input
            type="number"
            min={1}
            max={30}
            value={deadlineDays}
            onChange={(e) => setDeadlineDays(parseInt(e.target.value, 10) || 1)}
            className="w-24 border rounded px-3 py-1.5 text-right"
          />
          <span className="text-sm text-slate-500">ngày</span>
        </div>
      </div>

      <div className="border-t"></div>

      {/* M315 (2026-05-27): Section 4 — số lượt thi/tuần */}
      <div>
        <h3 className="text-sm font-semibold text-slate-800 mb-2">4. Số lượt làm bài kiểm tra mỗi tuần</h3>
        <p className="text-sm text-slate-600 mb-3">
          Mỗi NV được làm tối đa số lượt này trong 1 tuần (đếm chung cả 3 bộ thưởng).
          Reset 0h thứ Hai (giờ VN). Mặc định <strong>2 lượt/tuần</strong>. Tối thiểu 1, tối đa 7.
        </p>
        <div className="flex items-center gap-3">
          <label className="w-48 text-sm font-medium text-slate-700">Số lượt tối đa</label>
          <input
            type="number"
            min={1}
            max={7}
            value={attemptsPerWeek}
            onChange={(e) => setAttemptsPerWeek(parseInt(e.target.value, 10) || 1)}
            className="w-24 border rounded px-3 py-1.5 text-right"
          />
          <span className="text-sm text-slate-500">lượt/tuần</span>
        </div>
      </div>

      <div className="pt-4 border-t flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50"
        >
          {saving ? 'Đang lưu...' : 'Lưu ngưỡng'}
        </button>
      </div>
    </div>
  );
}

// ============================================================
// Tab 2: Câu trả lời NV
// ============================================================
function OversightTab() {
  // M140 (2026-05-12): Bỏ default date range — để rỗng = xem TẤT CẢ. Thêm filter NV.
  const [filter, setFilter] = useState({
    employeeId: '',
    bo: '',
    status: '',
    from: '',
    to: '',
    page: 1,
  });
  const [data, setData] = useState({ items: [], total: 0, page: 1, totalPages: 1, limit: 20 });
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState(null);
  // M140: dropdown employees
  const [employees, setEmployees] = useState([]);

  // Load employees 1 lần khi mở tab
  useEffect(() => {
    api.adminEmployees()
      .then((res) => setEmployees(res?.employees || res?.items || res || []))
      .catch(() => setEmployees([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    api.adminExamAttempts({
      employee_id: filter.employeeId,
      bo: filter.bo,
      status: filter.status,
      from: filter.from ? `${filter.from}T00:00:00Z` : '',
      to: filter.to ? `${filter.to}T23:59:59Z` : '',
      page: filter.page,
      limit: 20,
    })
      .then(setData)
      .catch((err) => toast.error(err?.data?.error || 'Lỗi tải danh sách'))
      .finally(() => setLoading(false));
  }, [filter]);

  function setDateRange(daysAgo) {
    const today = new Date().toISOString().slice(0, 10);
    if (daysAgo === null) {
      setFilter({ ...filter, from: '', to: '', page: 1 });
    } else {
      const from = new Date(Date.now() - daysAgo * 86400_000).toISOString().slice(0, 10);
      setFilter({ ...filter, from, to: today, page: 1 });
    }
  }

  return (
    <div>
      {/* Filter */}
      <div className="bg-white rounded-lg border p-4 mb-4 space-y-3">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs text-slate-500 mb-1">Nhân viên</label>
            <select
              value={filter.employeeId}
              onChange={(e) => setFilter({ ...filter, employeeId: e.target.value, page: 1 })}
              className="border rounded px-2 py-1.5 text-sm min-w-[200px]"
            >
              <option value="">Tất cả NV</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.full_name || e.fullName || e.name || `NV #${e.id}`}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Bộ</label>
            <select
              value={filter.bo}
              onChange={(e) => setFilter({ ...filter, bo: e.target.value, page: 1 })}
              className="border rounded px-2 py-1.5 text-sm"
            >
              <option value="">Tất cả 3 bộ thưởng</option>
              {REWARD_SETS.map((bo) => <option key={bo} value={bo}>{boLabel(bo)}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Trạng thái</label>
            <select
              value={filter.status}
              onChange={(e) => setFilter({ ...filter, status: e.target.value, page: 1 })}
              className="border rounded px-2 py-1.5 text-sm"
            >
              <option value="">Tất cả</option>
              {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Từ ngày</label>
            <input
              type="date"
              value={filter.from}
              onChange={(e) => setFilter({ ...filter, from: e.target.value, page: 1 })}
              className="border rounded px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Đến ngày</label>
            <input
              type="date"
              value={filter.to}
              onChange={(e) => setFilter({ ...filter, to: e.target.value, page: 1 })}
              className="border rounded px-2 py-1.5 text-sm"
            />
          </div>
          <div className="ml-auto text-sm text-slate-500">{data.total} bản ghi</div>
        </div>
        <div className="flex gap-2 items-center text-xs">
          <span className="text-slate-500">Lọc nhanh theo thời gian:</span>
          <button type="button" onClick={() => setDateRange(7)}
                  className="px-2 py-1 border rounded text-slate-700 hover:bg-slate-50">7 ngày</button>
          <button type="button" onClick={() => setDateRange(30)}
                  className="px-2 py-1 border rounded text-slate-700 hover:bg-slate-50">30 ngày</button>
          <button type="button" onClick={() => setDateRange(90)}
                  className="px-2 py-1 border rounded text-slate-700 hover:bg-slate-50">90 ngày</button>
          <button type="button" onClick={() => setDateRange(null)}
                  className="px-2 py-1 border rounded text-emerald-700 hover:bg-emerald-50 border-emerald-300">Tất cả thời gian</button>
        </div>
      </div>

      {/* Bảng list */}
      <div className="bg-white rounded-lg border overflow-x-auto">
        {loading ? (
          <div className="p-6 text-slate-500">Đang tải...</div>
        ) : data.items.length === 0 ? (
          <div className="p-6 text-slate-500">Không có bài kiểm tra nào trong khoảng thời gian này.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Nhân viên</th>
                <th className="px-3 py-2 text-left">Bộ</th>
                <th className="px-3 py-2 text-left">Trạng thái</th>
                <th className="px-3 py-2 text-left">Bắt đầu</th>
                <th className="px-3 py-2 text-center">Đúng / Tổng</th>
                <th className="px-3 py-2 text-center">Pass</th>
                <th className="px-3 py-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((row) => (
                <tr key={row.id} className="border-t hover:bg-slate-50">
                  <td className="px-3 py-2">{row.employee_name || `NV #${row.employee_id}`}</td>
                  <td className="px-3 py-2">{boLabel(row.bo)}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={row.status} />
                  </td>
                  <td className="px-3 py-2 text-slate-600">
                    {row.started_at ? new Date(row.started_at).toLocaleString('vi-VN') : '—'}
                  </td>
                  <td className="px-3 py-2 text-center font-mono">
                    {row.correct_count}/{row.total_questions}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {row.status === 'in_progress' ? (
                      <span className="text-slate-400 text-xs">—</span>
                    ) : row.passed ? (
                      <span className="text-emerald-600 font-medium">Đạt</span>
                    ) : (
                      <span className="text-red-500 font-medium">Chưa đạt</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => setDetailId(row.id)}
                      className="text-xs px-3 py-1 border rounded text-emerald-700 hover:bg-emerald-50"
                    >
                      Xem chi tiết
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {data.totalPages > 1 && (
        <div className="mt-3 flex items-center justify-center gap-2">
          <button
            onClick={() => setFilter({ ...filter, page: Math.max(1, filter.page - 1) })}
            disabled={data.page <= 1}
            className="px-3 py-1 border rounded text-sm disabled:opacity-40"
          >
            ← Trước
          </button>
          <span className="text-sm text-slate-600">Trang {data.page}/{data.totalPages}</span>
          <button
            onClick={() => setFilter({ ...filter, page: Math.min(data.totalPages, filter.page + 1) })}
            disabled={data.page >= data.totalPages}
            className="px-3 py-1 border rounded text-sm disabled:opacity-40"
          >
            Sau →
          </button>
        </div>
      )}

      {detailId && <AttemptDetailModal id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}

function StatusBadge({ status }) {
  const color = {
    in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
    submitted: 'bg-blue-50 text-blue-700 border-blue-200',
    regraded: 'bg-purple-50 text-purple-700 border-purple-200',
    abandoned: 'bg-slate-100 text-slate-500 border-slate-200',
  }[status] || 'bg-slate-100 text-slate-600 border-slate-200';
  return (
    <span className={`inline-block text-xs px-2 py-0.5 rounded border ${color}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

// ============================================================
// Modal chi tiết attempt
// ============================================================
function AttemptDetailModal({ id, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    api.adminExamAttemptDetail(id)
      .then(setData)
      .catch((err) => setError(err?.data?.error || 'Lỗi tải chi tiết'));
  }, [id]);

  function toggle(order) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(order)) next.delete(order);
      else next.add(order);
      return next;
    });
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[92vh] overflow-auto">
        <div className="border-b px-5 py-3 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="text-lg font-semibold">
            Chi tiết bài kiểm tra #{id}
            {data && (
              <span className="ml-3 text-sm font-normal text-slate-500">
                {data.attempt.employee_name} · {boLabel(data.attempt.bo)}
              </span>
            )}
          </h3>
          <button className="text-2xl leading-none text-slate-400 hover:text-slate-700" onClick={onClose}>×</button>
        </div>

        <div className="p-5">
          {error && <div className="text-red-500">{error}</div>}
          {!data && !error && <div className="text-slate-500">Đang tải...</div>}

          {data && (
            <>
              {/* Header summary */}
              <div className="bg-slate-50 rounded p-4 mb-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <Stat label="Trạng thái" value={<StatusBadge status={data.attempt.status} />} />
                <Stat label="Đúng / Tổng" value={`${data.stats.correct_count} / ${data.stats.total_questions}`} />
                <Stat label="Tỉ lệ" value={`${data.stats.correct_pct}%`} />
                <Stat
                  label="Pass"
                  value={
                    data.attempt.status === 'in_progress' ? (
                      <span className="text-slate-400">—</span>
                    ) : data.attempt.passed ? (
                      <span className="text-emerald-600 font-medium">Đạt (≥{data.attempt.threshold}%)</span>
                    ) : (
                      <span className="text-red-500 font-medium">Chưa đạt (cần ≥{data.attempt.threshold}%)</span>
                    )
                  }
                />
                <Stat label="Bắt đầu" value={data.attempt.started_at ? new Date(data.attempt.started_at).toLocaleString('vi-VN') : '—'} />
                <Stat label="Nộp" value={data.attempt.submitted_at ? new Date(data.attempt.submitted_at).toLocaleString('vi-VN') : '—'} />
                <Stat label="Điểm trung bình (admin)" value={data.attempt.score != null ? `${data.attempt.score}/100` : '—'} />
                <Stat label="AI chấm" value={data.attempt.grader_model || '—'} />
              </div>

              {/* Per câu */}
              <div className="space-y-2">
                {data.perQuestion.map((q) => (
                  <PerQuestionCard
                    key={q.question_order}
                    q={q}
                    expanded={expanded.has(q.question_order)}
                    onToggle={() => toggle(q.question_order)}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase text-slate-500 mb-0.5">{label}</div>
      <div>{value}</div>
    </div>
  );
}

function PerQuestionCard({ q, expanded, onToggle }) {
  const isCorrect = q.binary_correct;
  const headerColor = q.score == null
    ? 'border-slate-200 bg-slate-50'
    : isCorrect
      ? 'border-emerald-200 bg-emerald-50'
      : 'border-red-200 bg-red-50';

  return (
    <div className={`border rounded ${headerColor}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-2 text-left"
      >
        <span className="font-mono text-xs text-slate-500">#{q.question_order}</span>
        <span className="flex-1 truncate text-sm">{stripHtml(q.question)}</span>
        {q.score == null ? (
          <span className="text-xs text-slate-500">Chưa chấm</span>
        ) : isCorrect ? (
          <span className="text-sm font-medium text-emerald-700">Đúng</span>
        ) : (
          <span className="text-sm font-medium text-red-600">Chưa đúng</span>
        )}
        <span className="font-mono text-xs text-slate-500 ml-2">
          {q.score != null ? `${q.score}/100` : ''}
        </span>
        <span className="text-slate-400 text-xs">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="border-t bg-white p-4 space-y-3 text-sm">
          <Section title="Câu hỏi">
            <div dangerouslySetInnerHTML={{ __html: q.question || '<i>(rỗng)</i>' }} />
          </Section>
          <Section title="Đáp án chuẩn (canonical)">
            <div className="text-slate-700" dangerouslySetInnerHTML={{ __html: q.canonical_answer || '<i>(rỗng)</i>' }} />
          </Section>
          <Section title="Câu trả lời của NV">
            <div className="bg-slate-50 rounded p-3" dangerouslySetInnerHTML={{ __html: q.user_answer || '<i class="text-slate-400">(NV không trả lời)</i>' }} />
          </Section>

          {q.score != null && (
            <FeedbackPanel correct_points={q.correct_points} missing_points={q.missing_points} legacy_markdown={q.legacy_markdown} />
          )}

          <div className="flex flex-wrap gap-4 text-xs text-slate-500 pt-2 border-t">
            {q.time_spent_seconds != null && (
              <span>Thời gian làm: <b>{q.time_spent_seconds}</b>s</span>
            )}
            <span>Số lần paste bị chặn: <b className={q.paste_blocked_count > 0 ? 'text-red-600' : ''}>{q.paste_blocked_count}</b></span>
            {/* M139 hotfix: bỏ answered_at vì schema không có column này */}
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-xs uppercase font-medium text-slate-500 mb-1">{title}</div>
      {children}
    </div>
  );
}

function FeedbackPanel({ correct_points = [], missing_points = [], legacy_markdown = null }) {
  if (legacy_markdown) {
    return (
      <div>
        <div className="text-xs uppercase font-medium text-slate-500 mb-1">Nhận xét AI (legacy markdown)</div>
        <pre className="whitespace-pre-wrap text-sm text-slate-700 bg-slate-50 rounded p-3">{legacy_markdown}</pre>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div className="bg-emerald-50 border border-emerald-200 rounded p-4">
        <div className="text-base font-semibold text-emerald-800 mb-3">Chỗ đúng</div>
        {correct_points.length === 0 ? (
          <div className="text-sm text-slate-500 italic">Chưa có ý nào đúng</div>
        ) : (
          <ul className="space-y-2.5 list-none">
            {correct_points.map((p, i) => (
              <li key={i} className="text-sm text-slate-800 leading-relaxed pl-3 border-l-2 border-emerald-400">{p}</li>
            ))}
          </ul>
        )}
      </div>
      <div className="bg-rose-50 border border-rose-200 rounded p-4">
        <div className="text-base font-semibold text-rose-800 mb-3">Chỗ sai</div>
        {missing_points.length === 0 ? (
          <div className="text-sm text-slate-500 italic">Không có ý nào sai</div>
        ) : (
          <ul className="space-y-2.5 list-none">
            {missing_points.map((p, i) => (
              <li key={i} className="text-sm text-slate-800 leading-relaxed pl-3 border-l-2 border-rose-400">{p}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function stripHtml(html) {
  return String(html || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}
